import type { ReactNode } from 'react'

/**
 * The disclaimer band (style guide §7.5). Carries T.2, T.5 and SAT.7 verbatim.
 *
 * Never dismissible. The wireframes drew it with a dashed border; production uses a
 * solid border plus a 3px left bar — dashed is reserved for scope tags and the
 * dropzone.
 *
 * Mission 8 WP08 T039 (§7.12; FR-008, SC-008; post-tasks R15): `variant="caption"` is
 * the SAT.7 band inside an assistant answer. Only the text size changes; the fill,
 * border, glyph and `role="note"` are the default's, and no variant clips its text
 * (no truncate, line clamp, overflow or max height), so the sentence is always whole.
 */
export type DisclaimerBandVariant = 'default' | 'caption'

const TEXT: Record<DisclaimerBandVariant, string> = {
  default: 'text-body text-navy-950',
  caption: 'text-caption text-navy-950',
}

export function DisclaimerBand({
  children,
  variant = 'default',
}: {
  children: ReactNode
  variant?: DisclaimerBandVariant
}) {
  return (
    <div
      className="flex gap-3 rounded-lg border border-navy-300 border-l-[3px] border-l-navy-950 bg-navy-50 px-4 py-3"
      role="note"
      data-variant={variant}
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy-950 text-[11px] font-bold text-white"
      >
        §
      </span>
      <p className={TEXT[variant]}>{children}</p>
    </div>
  )
}
